import { Action } from 'actions'
import { combineReducers } from 'redux'
import { Reducer } from 'interfaces'
import {
  SHOW_CONTACT_EDIT_FORM,
  SHOW_PASSWORD_CHANGE_FORM,
  SHOW_PROFILE_EDIT_FORM,
  TOGGLE_CAR_EDIT,
  TOGGLE_HOME_EDIT,
  TOGGLE_SETTINGS_MODAL,
} from 'actions/settingsActions'
import { DELETE_CAR, DELETE_HOME, UNLINK_CAR, UNLINK_HOME } from 'actions/propertyActions'
import { INSIGHT_CTA_CLICK } from 'actions/insightsActions'
import { SettingsAppState, SettingsModalState, SettingsPropertyAppState } from 'interfaces/settings'
import { FORM_SUBMITTED_SUCCESSFULLY } from 'actions/formActions'
import { FORM_CARS_EDIT, FORM_CONTACT_EDIT, FORM_HOMES_EDIT, FORM_MEMBER_PROFILE } from 'constants/forms'
import { SETTINGS_URL } from 'tangle-shared'
import { DELETE_DEVICE } from 'actions/authActions'
import { PAGE_CHANGED } from 'actions/globalActions'
import { DELETE_MEMBER_ACCOUNT } from 'actions/memberActions'
import { FormNameType } from 'interfaces/form'
import { CANCEL_SUBSCRIPTION_CONFIRM } from 'actions/subscriptionActions'
import { NEW_PROPERTY_ID } from 'constants/member'


// ===========================================================================//
//                                  MEMBER                                    //
// ===========================================================================//

const passwordChangeFormVisible = (state = false, action: Action): boolean => {
  switch (action.type) {
    case SHOW_PASSWORD_CHANGE_FORM:
      return action.payload
    default:
      return state
  }
}

const profileEditFormVisible = (state = false, action: Action): boolean => {
  switch (action.type) {
    case INSIGHT_CTA_CLICK: {
      if (action.payload.type === 'edit' && action.payload.cta.field === 'income') {
        return true
      }

      return state
    }

    case SHOW_PROFILE_EDIT_FORM:
      return action.payload

    case FORM_SUBMITTED_SUCCESSFULLY: {
      if (action.payload.formName === FORM_MEMBER_PROFILE) {
        return false
      }

      return state
    }

    default:
      return state
  }
}

const contactEditFormVisible = (state = false, action: Action): boolean => {
  switch (action.type) {
    case SHOW_CONTACT_EDIT_FORM:
      return action.payload
    case FORM_SUBMITTED_SUCCESSFULLY:
      if (action.payload.formName === FORM_CONTACT_EDIT) {
        return false
      }
    default:
      return state
  }
}

// ===========================================================================//
//                                   HOME                                     //
// ===========================================================================//

const homeEditId = (state: number | null = null, action: Action): number | null => {
  switch (action.type) {
    case INSIGHT_CTA_CLICK: {
      if (action.payload.type === 'add_home') {
        return NEW_PROPERTY_ID
      }

      return state
    }

    case PAGE_CHANGED: {
      if (action.payload.next !== SETTINGS_URL) {
        return null
      }

      return state
    }

    case TOGGLE_HOME_EDIT:
      return action.payload

    case FORM_SUBMITTED_SUCCESSFULLY: {
      if (FORM_HOMES_EDIT === action.payload.formName as FormNameType) {
        return null
      }

      return state
    }

    case UNLINK_HOME:
      return state === action.payload.id ? null : state

    case DELETE_HOME:
      return state === action.payload ? null : state

    default:
      return state
  }
}

// ===========================================================================//
//                                    CAR                                     //
// ===========================================================================//

const carEditId = (state: number | null = null, action: Action): number | null => {
  switch (action.type) {
    case INSIGHT_CTA_CLICK: {
      if (action.payload.type === 'add_car') {
        return NEW_PROPERTY_ID
      }

      return state
    }

    case PAGE_CHANGED: {
      if (action.payload.next !== SETTINGS_URL) {
        return null
      }

      return state
    }

    case TOGGLE_CAR_EDIT:
      return action.payload

    case FORM_SUBMITTED_SUCCESSFULLY: {
      if (FORM_CARS_EDIT === action.payload.formName as FormNameType) {
        return null
      }

      return state
    }

    case UNLINK_CAR:
      return state === action.payload.id ? null : state

    case DELETE_CAR:
      return state === action.payload ? null : state

    default:
      return state
  }
}

// ===========================================================================//
//                                   DELETE ACCOUNT                           //
// ===========================================================================//

const settingsModalReducer: Reducer<SettingsModalState> = (state = null, action): SettingsModalState => {
  switch (action.type) {
    case TOGGLE_SETTINGS_MODAL:
      return action.payload

    case UNLINK_HOME: {
      const isHomeFromModal = state
        && state.type === 'unlink-home'
        && state.id === action.payload.id

      return isHomeFromModal ? null : state
    }

    case DELETE_HOME: {
      const isHomeFromModal = state
        && state.type === 'delete-home'
        && state.id === action.payload

      return isHomeFromModal ? null : state
    }

    case UNLINK_CAR: {
      const isCarFromModal = state
        && state.type === 'unlink-car'
        && state.id === action.payload.id

      return isCarFromModal ? null : state
    }

    case DELETE_CAR: {
      const isCarFromModal = state
        && state.type === 'delete-car'
        && state.id === action.payload

      return isCarFromModal ? null : state
    }

    case DELETE_DEVICE: {
      const isDeviceFromModal = state
        && state.type === 'delete-device'
        && state.id === action.payload

      return isDeviceFromModal ? null : state
    }

    case DELETE_MEMBER_ACCOUNT:
      return state && state.type === 'delete-member' ? null : state

    case CANCEL_SUBSCRIPTION_CONFIRM: {
      if (action.meta === 'request') {
        return {
          ...state,
          type: 'cancel-subscription',
        }
      }

      if (action.meta === 'success' && state && state.type === 'cancel-subscription') {
        return null
      }

      return state
    }

    default:
      return state
  }
}

export default combineReducers<SettingsAppState>({
  passwordChangeFormVisible,
  profileEditFormVisible,
  contactEditFormVisible,
  home: combineReducers<SettingsPropertyAppState>({
    editId: homeEditId,
  }),
  car: combineReducers<SettingsPropertyAppState>({
    editId: carEditId,
  }),
  modal: settingsModalReducer,
})
